import { BackendError, type DepositBackend, type DepositSummary, type MissingObject } from "./backend.js";
import type { DepositLimits } from "./limits.js";
import { objectsOf, toManifest, type PreparedDeposit } from "./prepare.js";

/**
 * Saving a deposit (docs/WEB_DEPOSIT.md §Sauvegarde):
 *
 *   1 start   the database records the expected files (or finds this attempt,
 *             an incomplete identical deposit to resume, or an exact duplicate)
 *   2 upload  only the objects the database reports missing; content-addressed
 *             and insert-only, so a retry or a concurrent upload is harmless
 *   3 complete the database checks that every expected object is stored with
 *             its size; only then is the deposit "saved"
 *
 * An interruption at any step leaves a deposit that the same client key, or the
 * same files dropped again (from any browser), resumes where it stopped.
 */
export type SaveProgress =
  | { phase: "starting" }
  | { phase: "uploading"; done: number; total: number }
  | { phase: "completing" };

export type SaveResult =
  | { status: "saved"; deposit: DepositSummary; resumed: boolean; uploaded: number }
  /** `unfinished`: the same carousel is being sent from other original files (another ZIP, or loose images), not yet complete. */
  | { status: "duplicate"; deposit: DepositSummary; unfinished: boolean }
  | { status: "conflict"; deposit: DepositSummary; paths: string[] }
  | { status: "interrupted"; deposit: DepositSummary | null; reason: string };

export interface SaveOptions {
  clientKey: string;
  note?: string | null;
  limits: DepositLimits;
  onProgress?: (progress: SaveProgress) => void;
  /** Parallel uploads (default 3) and attempts per object on network errors (default 3). */
  concurrency?: number;
  attempts?: number;
  sleep?: (ms: number) => Promise<void>;
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export async function saveDeposit(backend: DepositBackend, deposit: PreparedDeposit, options: SaveOptions): Promise<SaveResult> {
  const progress = options.onProgress ?? (() => {});
  const sleep = options.sleep ?? defaultSleep;
  const attempts = options.attempts ?? 3;
  const manifest = toManifest(deposit, options.note ?? null, options.limits);
  const objects = objectsOf(deposit);
  let summary: DepositSummary | null = null;
  try {
    progress({ phase: "starting" });
    const start = await backend.startDeposit(options.clientKey, manifest);
    summary = start.deposit;
    if (start.outcome === "duplicate") return { status: "duplicate", deposit: start.deposit, unfinished: false };
    let missing = start.missing;
    // resuming a deposit started from other originals: this selection cannot finish it
    if (missing.some((m) => !objects.has(m.sha256))) return { status: "duplicate", deposit: start.deposit, unfinished: true };
    let uploaded = 0;
    for (let round = 0; round < 2; round += 1) {
      const conflicts = missing.filter((m) => m.conflict).map((m) => m.storage_path);
      if (conflicts.length) return { status: "conflict", deposit: summary, paths: conflicts };
      uploaded += await uploadAll(backend, missing, objects, { progress, attempts, sleep, concurrency: options.concurrency ?? 3 });
      progress({ phase: "completing" });
      const done = await backend.completeDeposit(summary.id);
      summary = done.deposit;
      if (done.outcome === "complete") return { status: "saved", deposit: done.deposit, resumed: start.outcome !== "created", uploaded };
      missing = done.missing;
    }
    return { status: "interrupted", deposit: summary, reason: `${missing.length} fichier(s) toujours absent(s) après l'envoi` };
  } catch (error) {
    return { status: "interrupted", deposit: summary, reason: error instanceof Error ? error.message : String(error) };
  }
}

async function uploadAll(
  backend: DepositBackend,
  missing: MissingObject[],
  objects: ReturnType<typeof objectsOf>,
  opts: { progress: (p: SaveProgress) => void; attempts: number; sleep: (ms: number) => Promise<void>; concurrency: number },
): Promise<number> {
  const queue = [...missing];
  let done = 0;
  let created = 0;
  let failure: unknown = null;
  opts.progress({ phase: "uploading", done, total: queue.length });
  const worker = async () => {
    // after a failure, the other workers stop taking objects; every worker is awaited
    for (let item = queue.shift(); item && failure === null; item = queue.shift()) {
      const object = objects.get(item.sha256)!;
      for (let attempt = 1; ; attempt += 1) {
        try {
          if ((await backend.uploadObject(item.storage_path, object.data, object.mediaType)) === "created") created += 1;
          break;
        } catch (error) {
          const retriable = !(error instanceof BackendError) || error.code === "network";
          if (!retriable || attempt >= opts.attempts) {
            failure ??= error;
            return;
          }
          await opts.sleep(500 * 2 ** (attempt - 1));
        }
      }
      done += 1;
      opts.progress({ phase: "uploading", done, total: missing.length });
    }
  };
  await Promise.all(Array.from({ length: Math.min(opts.concurrency, queue.length || 1) }, worker));
  if (failure !== null) throw failure;
  return created;
}
